import { diatonicStep, midiOf, type Letter, LETTERS, type Spelled } from './notes';

/**
 * Intervals as (number, quality) rather than semitone counts, because the
 * curriculum constantly distinguishes an augmented fourth from a diminished
 * fifth. Semitones are derived, never stored.
 */

export type Quality = 'P' | 'M' | 'm' | 'A' | 'd' | 'AA' | 'dd';

export interface Interval {
  /** 1 = unison, 8 = octave, 15 = double octave. */
  number: number;
  quality: Quality;
}

/** Numbers that take perfect/augmented/diminished rather than major/minor. */
const PERFECT_STEPS = new Set([0, 3, 4]); // unison, fourth, fifth within an octave

export function isPerfectNumber(n: number): boolean {
  return PERFECT_STEPS.has((n - 1) % 7);
}

/** Semitones spanned by the natural (perfect/major) form of each step. */
const BASE_SEMITONES = [0, 2, 4, 5, 7, 9, 11];

export function semitonesOf(interval: Interval): number {
  const { number: n, quality } = interval;
  if (n < 1) throw new Error(`Interval number must be >= 1, got ${n}`);

  const step = (n - 1) % 7;
  const octaves = Math.floor((n - 1) / 7);
  const base = (BASE_SEMITONES[step] as number) + octaves * 12;
  const perfect = isPerfectNumber(n);

  const offset = ((): number => {
    switch (quality) {
      case 'P':
        if (!perfect) throw new Error(`${n} cannot be perfect`);
        return 0;
      case 'M':
        if (perfect) throw new Error(`${n} cannot be major`);
        return 0;
      case 'm':
        if (perfect) throw new Error(`${n} cannot be minor`);
        return -1;
      case 'A':
        return 1;
      case 'AA':
        return 2;
      case 'd':
        return perfect ? -1 : -2;
      case 'dd':
        return perfect ? -2 : -3;
      default: {
        const never: never = quality;
        throw new Error(`Unknown quality: ${String(never)}`);
      }
    }
  })();

  return base + offset;
}

export function isValidInterval(interval: Interval): boolean {
  try {
    semitonesOf(interval);
    return true;
  } catch {
    return false;
  }
}

/** The interval from `a` up to `b`. Both notes keep their written spelling. */
export function between(a: Spelled, b: Spelled): Interval {
  const steps = diatonicStep(b) - diatonicStep(a);
  const semis = midiOf(b) - midiOf(a);
  if (steps < 0) throw new Error('between(): second note must not be lower in letter order');

  const number = steps + 1;
  const natural = semitonesOf({ number, quality: isPerfectNumber(number) ? 'P' : 'M' });
  const diff = semis - natural;

  return { number, quality: qualityFromOffset(number, diff) };
}

function qualityFromOffset(number: number, diff: number): Quality {
  const perfect = isPerfectNumber(number);
  if (perfect) {
    switch (diff) {
      case 0:
        return 'P';
      case 1:
        return 'A';
      case 2:
        return 'AA';
      case -1:
        return 'd';
      case -2:
        return 'dd';
      default:
        throw new Error(`Interval too altered: ${number} offset ${diff}`);
    }
  }
  switch (diff) {
    case 0:
      return 'M';
    case -1:
      return 'm';
    case -2:
      return 'd';
    case -3:
      return 'dd';
    case 1:
      return 'A';
    case 2:
      return 'AA';
    default:
      throw new Error(`Interval too altered: ${number} offset ${diff}`);
  }
}

/** Transposes a written note by an interval, preserving correct spelling. */
export function transpose(
  note: Spelled,
  interval: Interval,
  direction: 'up' | 'down' = 'up',
): Spelled {
  const stepDelta = (interval.number - 1) * (direction === 'up' ? 1 : -1);
  const semitoneDelta = semitonesOf(interval) * (direction === 'up' ? 1 : -1);

  const targetStep = diatonicStep(note) + stepDelta;
  const letterIndex = ((targetStep % 7) + 7) % 7;
  const letter = LETTERS[letterIndex] as Letter;
  const octave = Math.floor(targetStep / 7);

  const targetMidi = midiOf(note) + semitoneDelta;
  // Whatever accidental makes this letter land on the target pitch.
  const naturalMidi = midiOf({ letter, alter: 0, octave });
  const alter = targetMidi - naturalMidi;

  if (Math.abs(alter) > 2) {
    throw new Error(
      `Transposing ${note.letter}${note.alter} by ${name(interval)} needs a triple accidental`,
    );
  }
  return { letter, alter, octave };
}

/** Inversion within the octave: the 9-rule, with qualities flipped. */
export function invert(interval: Interval): Interval {
  const simple = simplify(interval);
  const number = 9 - simple.number;
  const quality = ((): Quality => {
    switch (simple.quality) {
      case 'P':
        return 'P';
      case 'M':
        return 'm';
      case 'm':
        return 'M';
      case 'A':
        return 'd';
      case 'd':
        return 'A';
      case 'AA':
        return 'dd';
      case 'dd':
        return 'AA';
      default: {
        const never: never = simple.quality;
        throw new Error(String(never));
      }
    }
  })();
  return { number, quality };
}

/** Reduces a compound interval to within an octave. An octave stays an octave. */
export function simplify(interval: Interval): Interval {
  if (interval.number <= 8) return interval;
  let n = interval.number;
  while (n > 8) n -= 7;
  return { number: n, quality: interval.quality };
}

export function isCompound(interval: Interval): boolean {
  return interval.number > 8;
}

/** Adds octaves to a simple interval (a 3rd plus one octave is a 10th). */
export function compound(interval: Interval, octaves: number): Interval {
  return { number: interval.number + 7 * octaves, quality: interval.quality };
}

/* ------------------------------------------------------------------ naming */

const ORDINALS = [
  '',
  'unison',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'octave',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
  'thirteenth',
  'fourteenth',
  'fifteenth',
];

const QUALITY_WORDS: Record<Quality, string> = {
  P: 'perfect',
  M: 'major',
  m: 'minor',
  A: 'augmented',
  d: 'diminished',
  AA: 'doubly augmented',
  dd: 'doubly diminished',
};

/** `'short'` gives "M3"; `'long'` gives "major third". */
export function name(interval: Interval, style: 'short' | 'long' = 'short'): string {
  if (style === 'short') return `${interval.quality}${interval.number}`;
  const ordinal = ORDINALS[interval.number] ?? `${interval.number}th`;
  return `${QUALITY_WORDS[interval.quality]} ${ordinal}`;
}

/** Just the number, for the beginner band that ignores quality. */
export function numberName(interval: Interval): string {
  return ORDINALS[interval.number] ?? `${interval.number}th`;
}

const PARSE_RE = /^(P|M|m|A{1,2}|d{1,2})\s*(\d{1,2})$/;

export function parse(text: string): Interval {
  const match = PARSE_RE.exec(text.trim());
  if (match === null) throw new Error(`Cannot parse interval: "${text}"`);
  const quality = match[1] as Quality;
  const number = Number.parseInt(match[2] as string, 10);
  const interval: Interval = { number, quality };
  semitonesOf(interval); // throws when the combination is impossible
  return interval;
}

/** Converts to tonal's `"3M"` ordering, for handing values to that library. */
export function toTonal(interval: Interval): string {
  return `${interval.number}${interval.quality}`;
}

/* ------------------------------------------------------------- consonance */

export type Consonance = 'perfect' | 'imperfect' | 'dissonant';

export function consonance(interval: Interval): Consonance {
  const s = simplify(interval);
  if (s.quality === 'P' && (s.number === 1 || s.number === 5 || s.number === 8)) return 'perfect';
  if (s.quality === 'P' && s.number === 4) return 'dissonant'; // by common-practice rule
  if ((s.quality === 'M' || s.quality === 'm') && (s.number === 3 || s.number === 6)) {
    return 'imperfect';
  }
  return 'dissonant';
}

export function isTritone(interval: Interval): boolean {
  const s = simplify(interval);
  return (s.number === 4 && s.quality === 'A') || (s.number === 5 && s.quality === 'd');
}

/** All spellings of an interval with the same semitone count, excluding itself. */
export function enharmonicIntervals(interval: Interval): Interval[] {
  const target = semitonesOf(interval);
  const out: Interval[] = [];
  const qualities: Quality[] = ['P', 'M', 'm', 'A', 'd', 'AA', 'dd'];

  for (let number = 1; number <= 15; number += 1) {
    for (const quality of qualities) {
      const candidate: Interval = { number, quality };
      if (!isValidInterval(candidate)) continue;
      if (semitonesOf(candidate) !== target) continue;
      if (candidate.number === interval.number && candidate.quality === interval.quality) continue;
      out.push(candidate);
    }
  }
  return out;
}

/** The common simple intervals, in ascending order. Used by generators. */
export const SIMPLE_INTERVALS: readonly Interval[] = [
  { number: 1, quality: 'P' },
  { number: 2, quality: 'm' },
  { number: 2, quality: 'M' },
  { number: 3, quality: 'm' },
  { number: 3, quality: 'M' },
  { number: 4, quality: 'P' },
  { number: 4, quality: 'A' },
  { number: 5, quality: 'd' },
  { number: 5, quality: 'P' },
  { number: 6, quality: 'm' },
  { number: 6, quality: 'M' },
  { number: 7, quality: 'm' },
  { number: 7, quality: 'M' },
  { number: 8, quality: 'P' },
];
